// One device frame: a Google-style result card drawn from the engine's lines,
// the "Your text" strip, badges, warnings and Copy as text (spec §7.2).
import { boldRuns, copyAsText, keptEnd as keptEndOf, STATUS_WORD, type Config, type FieldResult, type Overall, type Profile, type ProfileResult, type Warning, type WarningCode } from '../engine';
import { btn, copyText, h, icon, ICONS } from './dom';
import { toast } from './toast';
import { track } from './track';

export interface FrameInput {
  profile: Profile;
  title?: FieldResult;
  description?: FieldResult;
  url: string;
  brand: string;
  query: string;
  showHidden: boolean;
  config: Config;
}

export function badge(status: Overall, text?: string): HTMLElement {
  return h('span', { class: `badge badge-${status}` }, h('i', { class: 'dot', 'aria-hidden': 'true' }), text ?? STATUS_WORD[status]);
}

export function profileTag(p: Profile): HTMLElement | null {
  if (p.status === 'verified') return null;
  return h('span', { class: `tag tag-${p.status}`, title: p.status === 'experimental' ? 'Experimental: layout not yet confirmed on a real device' : 'Unverified: assumed from a nearby verified device' },
    p.status === 'experimental' ? 'Experimental' : 'Unverified');
}

const SHORT: Record<WarningCode, string> = {
  BRAND_HIDDEN: 'Brand hidden', KEYWORD_HIDDEN: 'Keyword hidden', DANGLING_END: 'Dangling end',
  LOW_SHOWN: 'Low shown', FONT_SENSITIVE: 'Font sensitive', TIGHT: 'Tight',
};

export function warningLabel(w: Pick<Warning, 'code' | 'detail'>): string {
  if (w.code === 'DANGLING_END' && w.detail) return `Ends on "${w.detail}"`;
  if ((w.code === 'BRAND_HIDDEN' || w.code === 'KEYWORD_HIDDEN') && w.detail) return `${SHORT[w.code]}: ${w.detail}`;
  return SHORT[w.code];
}

/** A warning label. Its full message shows in a Basecoat tooltip on hover, and is read out by
 *  screen readers from visually hidden text (the tooltip is mouse-only). */
export function warningChip(w: Pick<Warning, 'code' | 'detail' | 'severity'> & { message?: string }): HTMLElement {
  return h('span', { class: `chip chip-${w.severity}`, 'data-tooltip': w.message, 'data-align': 'start' },
    icon(ICONS.alert, 12), warningLabel(w), w.message ? h('span', { class: 'sr-only' }, `: ${w.message}`) : null);
}

function site(url: string, brand: string) {
  let host = 'example.com', path = '';
  try {
    const u = new URL(/^https?:\/\//.test(url) ? url : `https://${url || 'example.com'}`);
    host = u.hostname.replace(/^www\./, '');
    path = u.pathname.split('/').filter(Boolean).join(' › ');
  } catch { /* keep defaults */ }
  const name = brand || host.split('.')[0].replace(/^./, c => c.toUpperCase());
  return { host, path, name };
}

/** Draw lines, bolding query terms in descriptions. `sent` is what Google sent. */
function drawLines(cls: string, lines: string[], sent: string, font: string, lh: number, bold: { start: number; end: number }[], boldFont: string | undefined, fromOffset: number, feature?: boolean): { els: HTMLElement[]; next: number } {
  let pos = fromOffset;
  const els = lines.map(text => {
    const at = sent.indexOf(text, pos);
    const start = at >= 0 ? at : pos;
    pos = start + text.length;
    const el = h('div', { class: cls, style: { font, lineHeight: `${lh}px`, height: `${lh}px`, fontFeatureSettings: feature ? '"calt" 0, "liga" 0' : '' } });
    if (!bold.length || !boldFont) { el.textContent = text; return el; }
    let i = 0;
    for (const b of bold) {
      const s = Math.max(b.start - start, 0), e = Math.min(b.end - start, text.length);
      if (e <= 0 || s >= text.length) continue;
      if (s > i) el.append(text.slice(i, s));
      el.append(h('b', { style: { font: boldFont } }, text.slice(s, e)));
      i = e;
    }
    if (i < text.length) el.append(text.slice(i));
    return el;
  });
  return { els, next: pos };
}

function fieldLines(f: FieldResult, r: ProfileResult, p: Profile, which: 'title' | 'description', query: string, cfg: Config, showHidden: boolean): HTMLElement {
  const L = p[which];
  const bold = which === 'description' ? boldRuns(f.googleCut.sent, query, cfg.boldStopwords) : [];
  const cls = which === 'title' ? 'g-title' : 'g-desc';
  const vis = drawLines(cls, r.lines, f.googleCut.sent, L.font, L.lineHeight, bold, L.boldFont, 0, which === 'title');
  const wrap = h('div', { class: `g-${which}-block` }, vis.els);
  if (r.hiddenLines.length) {
    const last = vis.els[vis.els.length - 1];
    if (last) last.append(h('span', { class: 'g-clamp', 'aria-hidden': 'true' }, '…'));
    if (showHidden) {
      const hid = drawLines(cls, r.hiddenLines, f.googleCut.sent, L.font, L.lineHeight, bold, L.boldFont, vis.next, which === 'title');
      wrap.append(
        h('div', { class: 'g-rule' }, h('span', null, 'Hidden by line limit')),
        h('div', { class: 'g-hidden', 'aria-label': 'Lines hidden by the line limit' }, hid.els),
      );
    }
  }
  return wrap;
}

/** "Your text": shown words, ✂, then struck-through hidden and cut spans. */
export function strip(f: FieldResult, r: ProfileResult, cfg: Config): HTMLElement {
  const o = f.original;
  const shownEnd = r.shownText.length;
  const keptEnd = keptEndOf(f, cfg);
  const byLimit = o.slice(shownEnd, Math.max(shownEnd, keptEnd)).trim();
  const byGoogle = o.slice(Math.max(shownEnd, keptEnd)).trim();
  return h('p', { class: 'strip' },
    h('span', { class: 'strip-label' }, f.field === 'title' ? 'Title' : 'Description'),
    h('span', { class: 'strip-shown' }, r.shownText),
    r.notShownText ? [' ', h('span', { class: 'scissors', 'aria-label': 'cut here' }, '✂'), ' '] : null,
    byLimit ? h('s', { class: 'hid-limit', title: 'Hidden by line limit' }, byLimit) : null,
    byLimit && byGoogle ? ' ' : null,
    byGoogle ? h('s', { class: 'hid-google', title: 'Cut by Google' }, byGoogle) : null,
  );
}

function measureNote(f: FieldResult, r: ProfileResult): string {
  if (r.status === 'cut') return `${r.shownPct}% shown`;
  return `${Math.round(r.roomLeftPx)}px room left`;
}

export function frameWidth(p: Profile): number {
  return p.deviceClass === 'mobile' ? p.viewportWidth : p.description.width + 32;
}

export function deviceFrame(i: FrameInput): HTMLElement {
  const p = i.profile;
  const tr = i.title?.results.find(r => r.profile === p.id);
  const dr = i.description?.results.find(r => r.profile === p.id);
  const s = site(i.url, i.brand);
  const fw = frameWidth(p);
  const pad = p.deviceClass === 'mobile' ? (p.viewportWidth - p.title.width) / 2 : 16;
  const fallback = !!(tr?.fontFallback?.length || dr?.fontFallback?.length);

  const card = h('div', { class: 'g-card', style: { width: `${fw}px`, padding: `14px ${pad}px 16px` }, lang: 'en' },
    h('div', { class: 'g-site' },
      h('span', { class: 'g-fav', 'aria-hidden': 'true' }, s.name.slice(0, 1)),
      h('span', { class: 'g-site-text' },
        h('span', { class: 'g-site-name' }, s.name),
        h('span', { class: 'g-url' }, `https://${s.host}${s.path ? ' › ' + s.path : ''}`))),
    i.title && tr ? fieldLines(i.title, tr, p, 'title', i.query, i.config, i.showHidden) : null,
    i.description && dr ? fieldLines(i.description, dr, p, 'description', i.query, i.config, i.showHidden) : null,
  );

  const warns = new Map<string, Warning>();
  for (const f of [i.title, i.description]) {
    if (!f) continue;
    const r = f.results.find(x => x.profile === p.id);
    for (const w of f.warnings) if (w.code !== 'TIGHT' && w.profiles.includes(p.id) && r?.warnings.includes(w.code)) warns.set(`${f.field}:${w.code}`, w);
  }

  const copyBtn = h('button', btn('ghost', 'sm'), icon(ICONS.copy, 14), 'Copy as text');
  copyBtn.addEventListener('click', async () => {
    const text = [i.title && tr ? copyAsText(i.title, tr, p) : '', i.description && dr ? copyAsText(i.description, dr, p) : ''].filter(Boolean).join('\n\n');
    const ok = await copyText(text);
    if (ok) {
      track('copy_as_text', { device: p.id });
      toast(`Copied ${p.label} as text`);
      return;
    }
    copyBtn.lastChild!.textContent = 'Copy failed';
    setTimeout(() => { copyBtn.lastChild!.textContent = 'Copy as text'; }, 1500);
  });

  return h('article', { class: 'frame', 'data-profile': p.id, style: { '--frame-w': `${fw}px` } as any },
    h('header', { class: 'frame-head' },
      h('span', { class: 'frame-name' }, p.label),
      h('span', { class: 'frame-dim mono' }, `${p.viewportWidth}px`),
      profileTag(p),
      fallback ? h('span', { class: 'tag tag-experimental', title: 'A font this profile needs is not installed in this browser, so a fallback font measured it.' }, 'Font fallback') : null),
    h('div', { class: 'frame-scroll' }, card),
    h('div', { class: 'frame-foot' },
      i.title && tr ? strip(i.title, tr, i.config) : null,
      i.description && dr ? strip(i.description, dr, i.config) : null,
      h('div', { class: 'frame-meta' },
        tr ? h('span', { class: 'meta-item' }, h('span', { class: 'meta-k' }, 'Title'), badge(tr.status), h('span', { class: 'mono meta-v' }, measureNote(i.title!, tr))) : null,
        dr ? h('span', { class: 'meta-item' }, h('span', { class: 'meta-k' }, 'Description'), badge(dr.status), h('span', { class: 'mono meta-v' }, measureNote(i.description!, dr))) : null),
      warns.size ? h('div', { class: 'chips' }, [...warns.values()].map(warningChip)) : null,
      h('div', { class: 'frame-actions' }, copyBtn)),
  );
}

export function stripLegend(): HTMLElement {
  return h('p', { class: 'legend' },
    h('span', null, h('span', { class: 'scissors' }, '✂'), ' cut point'),
    h('span', null, h('s', { class: 'legend-limit' }, 'hidden'), ' by line limit'),
    h('span', null, h('s', { class: 'legend-google' }, 'cut'), ' by Google'),
  );
}
